import { from, of, forkJoin, map as rxjsMap, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { PackageService } from "src/app/services/package.service";

import {
  Client,
  DealerResponse,
  ExternalTyreUrlRequest,
  InsuranceCompanyRequest,
  PackageResponse,
  VehicleSearchResponse,
  VehicleSegmentRequest,
  WorkshopSystemEnum,
  PackageTypeEnum,
  PackageRequest,
  BusinessProposalStatusEnum,
  InsuranceCompanyResponse,
  BusinessProposalResponse,
  PackageTagEnum,
  BookingResourceRequest,
  PropertyEnum,
  CreateBookingErrorResponse,
  SwaggerException,
  BookingErrorEnum,
  DealerOpenHourResponse
} from "src/domain/client";
import moment from "moment";
import { BusinessProposalService } from "src/app/services/business-proposal.service";
import { BookingState } from "../booking.state";
import { DatePipe } from "@angular/common";
import { IBookingError } from "./booking.modals";

function _groupProposalPackages(proposal: BusinessProposalResponse) {
  const extraServicePackageType = [
    PackageTypeEnum.PPS,
    PackageTypeEnum.WashAndRecond,
    PackageTypeEnum.ServiceAdditional,
    PackageTypeEnum.DealerExtra
  ];
  return (proposal.packages || []).reduce(
    (acc, p) => {
      if (extraServicePackageType.includes(p.type)) {
        acc.extraServicePackages.push(p);
      } else {
        acc.other.push(p);
      }
      return acc;
    },
    { extraServicePackages: [], other: [] }
  );
}

function _mergeBasePackages(
  standardPackages: PackageResponse[],
  proposalPackages: PackageResponse[]
): PackageResponse[] {
  const PREFER_STANDARD_VERSION_FOR_TYPES: PackageTypeEnum[] = [
    PackageTypeEnum.Windshield,
    PackageTypeEnum.DropoffOption
  ];

  const finalPackages = new Map<string, PackageResponse>();
  standardPackages.forEach((p) => {
    const isServicePackage =
      p.type === PackageTypeEnum.OriginalService ||
      p.type === PackageTypeEnum.BaseService;
    if (!isServicePackage) {
      finalPackages.set(p.id, p);
    }
  });

  proposalPackages.forEach((proposalPkg) => {
    const preferStandard = PREFER_STANDARD_VERSION_FOR_TYPES.includes(
      proposalPkg.type
    );
    if (!preferStandard) {
      finalPackages.set(proposalPkg.id, proposalPkg);
    }
  });

  return [...finalPackages.values()];
}

export function fetchSpecificationsData$(
  packageService: PackageService,
  businessProposalService: BusinessProposalService,
  carInfo: VehicleSearchResponse,
  selectedDealer: DealerResponse,
  systems: WorkshopSystemEnum[],
  odometer: number,
  enabledServiceTypes: PackageTypeEnum[]
): Observable<{
  basePackages: PackageResponse[];
  additionalPackages: PackageResponse[];
  segment: number;
  urlResponse: any;
  providers: InsuranceCompanyResponse[];
  lights: any[];
}> {
  const client = new Client();
  const packageRequestData = {
    chassiNumber: carInfo.chassiNumber,
    dealerNumber: selectedDealer.number,
    odometer,
    systems
  };

  const basePackages$ = packageService.getAllPackagesForStep4(
    packageRequestData,
    enabledServiceTypes
  );
  const additionalPackages$ = from(
    client.apiV2PackageServiceAdditional(
      new PackageRequest({ ...packageRequestData, odometer: odometer * 10 })
    )
  );
  const segment$ = from(
    client.apiV2VehicleGetSegment(
      new VehicleSegmentRequest({
        chassiNumber: carInfo.chassiNumber,
        dealerNumber: selectedDealer.number
      })
    )
  );
  const tyreUrl$ = from(
    client.apiV2DealerExternalTyre(
      new ExternalTyreUrlRequest({
        chassiNumber: carInfo.chassiNumber,
        dealerNumber: selectedDealer.number
      })
    )
  );
  const insuranceProviders$ = systems.includes(WorkshopSystemEnum.Windscreen)
    ? from(
        client.apiV2InsuranceCompanyActiveInsuranceCompanies(
          new InsuranceCompanyRequest({ dealerNumber: selectedDealer.number })
        )
      )
    : of([]);
  const warningLights$ = systems.includes(WorkshopSystemEnum.Troubleshooting)
    ? from(client.apiV2VehicleAllWarningLights())
    : of([]);

  return forkJoin({
    basePackages: basePackages$,
    additionalPackages: additionalPackages$,
    segment: segment$,
    urlResponse: tyreUrl$,
    providers: insuranceProviders$,
    lights: warningLights$
  }).pipe(
    map((results) => {
      let finalBasePackages = results.basePackages;
      let finalAdditionalPackages = results.additionalPackages;
      const proposal = businessProposalService.businessProposal$.value;

      if (businessProposalService.isBusinessProposalActive && proposal) {
        const grouped = _groupProposalPackages(proposal);
        finalBasePackages = _mergeBasePackages(
          results.basePackages,
          grouped.other
        );
        const filtered = filterAdditionalPackages(
          results.additionalPackages,
          grouped.extraServicePackages,
          proposal
        );
        finalAdditionalPackages = [
          ...filtered,
          ...grouped.extraServicePackages
        ];

        finalAdditionalPackages = sortAdditionalPackages(
          finalAdditionalPackages
        );
      }

      return {
        ...results,
        basePackages: finalBasePackages,
        additionalPackages: finalAdditionalPackages
      };
    })
  );
}

export function sortAdditionalPackages(
  packages: PackageResponse[]
): PackageResponse[] {
  return packages.sort((a, b) => {
    const aIsRecommended = a.tags.includes(PackageTagEnum.Recommended) ? 0 : 1;
    const bIsRecommended = b.tags.includes(PackageTagEnum.Recommended) ? 0 : 1;
    if (aIsRecommended !== bIsRecommended) {
      return aIsRecommended - bIsRecommended;
    }
    return a.name.localeCompare(b.name);
  });
}

export function filterAdditionalPackages(
  allAdditionalPackages: PackageResponse[],
  proposalExtraServicePackages: PackageResponse[],
  proposal: { businessProposalStatus?: BusinessProposalStatusEnum } | null
): PackageResponse[] {
  if (proposal?.businessProposalStatus !== BusinessProposalStatusEnum.Expired) {
    return allAdditionalPackages.filter((pkg) => {
      const isAlreadyInProposal = proposalExtraServicePackages.some((p) => {
        if (pkg.packageExternalId) {
          return p.packageExternalId === pkg.packageExternalId;
        }
        return p.id === pkg.id;
      });
      return !isAlreadyInProposal;
    });
  } else {
    return allAdditionalPackages;
  }
}

export function _getResourceRequest(
  state: BookingState,
  datePipe: DatePipe
): BookingResourceRequest | null {
  if (!state.selectedDate) return null;

  const dropoffOption = state.basePackages?.find(
    (p) =>
      p.type === PackageTypeEnum.DropoffOption &&
      p.id === state.selectedDropoffOptionId
  );
  const usesTimeSlot = dropoffOption?.properties.includes(
    PropertyEnum.TimeSlot
  );
  const selectedDate = new Date(state.selectedDate);

  let start: Date | null = null;
  let end: Date | null = null;
  let mechanic: string | null = null;

  if (usesTimeSlot && state.selectedTime) {
    const selectedTimeSlot = state.availableTimes
      ?.find(
        (d) => new Date(d.date).toDateString() === selectedDate.toDateString()
      )
      ?.timeSlots.find((t) => t.slot === state.selectedTime);

    if (selectedTimeSlot?.mechanicDurations[0]) {
      const { mechanicId, duration } = selectedTimeSlot.mechanicDurations[0];
      const hours = parseInt(duration.split(":")[0], 10);
      const minutes = parseInt(duration.split(":")[1], 10);
      const durationMs = hours * 60 * 60 * 1000 + minutes * 60 * 1000;

      start = new Date(
        selectedDate.setHours(
          parseInt(state.selectedTime.split(":")[0]),
          parseInt(state.selectedTime.split(":")[1])
        )
      );
      end = new Date(start.getTime() + durationMs);
      mechanic = mechanicId;
    }
  }

  return new BookingResourceRequest({
    customerAllowedToWait: true,
    usesTimeSlot,
    date: datePipe.transform(selectedDate, "yyyy-MM-dd"),
    startDateTime: start,
    endDateTime: end,
    resourceGroupId: mechanic || undefined
  });
}

export function _parseCreateBookingError(
  reason: CreateBookingErrorResponse | SwaggerException
): IBookingError {
  if (reason instanceof CreateBookingErrorResponse) {
    const timeslotErrors = [
      BookingErrorEnum.SelectedDayUnavailable,
      BookingErrorEnum.NoTimeSlotFoundForTimeSlotBooking,
      BookingErrorEnum.SelectedDropoffOptionIsNotAvailableThatDay,
      BookingErrorEnum.SelectedTimeUnavailable
    ];
    return timeslotErrors.includes(reason.bookingError)
      ? {
          title: "request-fail.timeslot.title",
          subtitle: "",
          returnText: "request-fail.timeslot.return",
          returnToPage: 6,
          returnButtonAbove: true
        }
      : null;
  }

  return null;
}

export function _getWorkshopHoursForDate(
  dealer: DealerResponse | null | undefined,
  selectedDate: string | Date | null | undefined
): DealerOpenHourResponse | null {
  if (!dealer?.dealerOpenHourResponses?.length || !selectedDate) {
    return null;
  }
  const dayOfWeek = moment(selectedDate).day();
  const swedishDayNames = [
    "söndag",
    "måndag",
    "tisdag",
    "onsdag",
    "torsdag",
    "fredag",
    "lördag"
  ];
  const englishDayNames = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday"
  ];

  return (
    dealer.dealerOpenHourResponses.find((h) => {
      if (h.dayOfWeek !== undefined && h.dayOfWeek !== null) {
        if (
          h.dayOfWeek === dayOfWeek ||
          (dayOfWeek === 0 && h.dayOfWeek === 7)
        ) {
          return true;
        }
      }
      if (h.nameOfDay) {
        const name = h.nameOfDay.trim().toLowerCase();
        if (
          name === swedishDayNames[dayOfWeek] ||
          name === englishDayNames[dayOfWeek]
        ) {
          return true;
        }
      }
      return false;
    }) ?? null
  );
}

export function _formatTimeString(
  time: string | undefined | null
): string | null {
  if (!time) return null;
  const m = moment(time, ["HH:mm:ss", "HH:mm", "H:mm"]);
  return m.isValid() ? m.format("HH:mm") : time;
}

export function _getEbuBookingTimes(
  dealer: DealerResponse | null | undefined,
  selectedDate: string | Date | null | undefined,
  fallbackStartTime?: string | null
): { startTime: string; endTime: string } {
  const openHour = _getWorkshopHoursForDate(dealer, selectedDate);
  const isClosed = openHour?.closed === true;

  const workshopStartTime = !isClosed
    ? _formatTimeString(openHour?.openTime)
    : null;
  const workshopLeaveTime = !isClosed
    ? _formatTimeString(openHour?.closeTime)
    : null;

  const startTime = workshopStartTime ?? fallbackStartTime ?? "08:00";
  const endTime =
    workshopLeaveTime ??
    moment(startTime, "HH:mm").add(8, "hour").format("HH:mm");

  return { startTime, endTime };
}

export function _getEbuBookingDates(
  dealer: DealerResponse | null | undefined,
  selectedDate: string | Date | null | undefined,
  fallbackStartTime?: string | null
): { startDate: Date; endDate: Date } | null {
  if (!selectedDate) {
    return null;
  }

  const { startTime, endTime } = _getEbuBookingTimes(
    dealer,
    selectedDate,
    fallbackStartTime
  );

  const dateStr = moment(selectedDate).format("YYYY-MM-DD");
  const startDate = moment(
    `${dateStr} ${startTime}`,
    "YYYY-MM-DD HH:mm"
  ).toDate();
  const endMoment = moment(`${dateStr} ${endTime}`, "YYYY-MM-DD HH:mm");
  if (endMoment.isBefore(startDate)) {
    endMoment.add(1, "day");
  }
  const endDate = endMoment.toDate();

  return { startDate, endDate };
}
